import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useDispatch } from 'react-redux';
import {
  MfaVerifySetupRequest,
  MfaVerifySetupResponse,
} from '@classpass/protobuf-definitions/lib/web/models/partners/user_mfa';

import { Card, CardSection } from 'components/Card';
import Message from 'components/Message';

import { bem } from 'helpers/class-name';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';

import URLS from 'constants/urls';
import { ICONS } from 'constants/icons';

import { closeModal } from 'components/Modal';
import { createSuccessSnackbar, createErrorSnackbar } from 'components/Snackbar';
import When from 'components/When';
import Button from 'components/Button';
import QRCodeReadStep from './QRCodeReadStep';
import EnterCodeStep from './EnterCodeStep';
import CopyRecoveryCodesStep from './CopyRecoveryCodesStep';

import { messages } from './MFASetupModal.messages';
import './MFASetupModal.scss';

const { block, element } = bem('mfa-setup-modal');

const STEPS = {
  QR_CODE_READ: 'QR_CODE_READ',
  ENTER_CODE: 'ENTER_CODE',
  SETUP_COMPLETE: 'SETUP_COMPLETE',
  VALIDATION_EXPIRED: 'VALIDATION_EXPIRED',
};

const MODAL_TITLE = {
  [STEPS.QR_CODE_READ]: messages.setupTitle,
  [STEPS.ENTER_CODE]: messages.completeSetupTitle,
  [STEPS.SETUP_COMPLETE]: messages.mfaActiveTitle,
  [STEPS.VALIDATION_EXPIRED]: messages.validationExpiredTitle,
};

export const ERROR_TYPES = {
  EXPIRED: 'has expired',
};

const MFASetupModal = ({ privateKey, qrcodeURI, onMFAActivatedSucessfully, onRestartSetup }) => {
  const [currentStep, setCurrentStep] = React.useState(STEPS.QR_CODE_READ);
  const [recoveryCodes, setRecoveryCodes] = React.useState([]);
  const [isValidatingOTP, setIsValidatingOTP] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const dispatch = useDispatch();

  const handleCopySecretToClipboard = () => {
    try {
      navigator.clipboard.writeText(privateKey);
      dispatch(createSuccessSnackbar(messages.copiedToClipboard));
    } catch {
      dispatch(createErrorSnackbar(messages.copiedToClipboardError));
    }
  };

  const handleOTPCodeEntered = async code => {
    setIsValidatingOTP(true);

    try {
      const requestBody = MfaVerifySetupRequest.toJSON({ totp_code: code });
      const { data } = await httpClient.post(URLS.user.mfa.verifyOTP, requestBody);
      const verifyResponse = MfaVerifySetupResponse.fromJSON(data);

      setRecoveryCodes(verifyResponse.private_recovery_codes);
      setIsValidatingOTP(false);
      dispatch(createSuccessSnackbar(messages.setupSuccessful));
      setCurrentStep(STEPS.SETUP_COMPLETE);
      onMFAActivatedSucessfully();
    } catch (error) {
      const errorMessage = error.response?.data?.message || '';
      if (errorMessage.includes(ERROR_TYPES.EXPIRED)) {
        dispatch(createErrorSnackbar(messages.errorOTPExpired));
        setCurrentStep(STEPS.VALIDATION_EXPIRED);
      } else {
        setHasError(true);
        reportError('Error validating OTP code on MFA setup', error);
        dispatch(createErrorSnackbar(messages.errorValidateOTP));
      }

      setIsValidatingOTP(false);
    }
  };

  return (
    <Card className={block()}>
      <CardSection flush className={element('header')}>
        <Message as="h1" message={MODAL_TITLE[currentStep]} />

        <When predicate={currentStep !== STEPS.SETUP_COMPLETE && !isValidatingOTP}>
          <div className={element('close-button')}>
            <button
              type="button"
              onClick={() => dispatch(closeModal())}
              name="close-button"
              aria-label="Close MFA Setup Modal"
              className={element('close-icon')}>
              <Isvg src={ICONS.cross} />
            </button>
          </div>
        </When>
      </CardSection>

      <When predicate={currentStep === STEPS.QR_CODE_READ}>
        <QRCodeReadStep
          privateKey={privateKey}
          qrcodeURI={qrcodeURI}
          onCopyToClipboard={handleCopySecretToClipboard}
          onContinue={() => setCurrentStep(STEPS.ENTER_CODE)}
        />
      </When>
      <When predicate={currentStep === STEPS.ENTER_CODE}>
        <EnterCodeStep
          isValidatingOTP={isValidatingOTP}
          onGoBack={() => setCurrentStep(STEPS.QR_CODE_READ)}
          onOTPCodeEnter={handleOTPCodeEntered}
          hasError={hasError}
          onClearError={() => setHasError(false)}
        />
      </When>
      <When predicate={currentStep === STEPS.SETUP_COMPLETE}>
        <CopyRecoveryCodesStep recoveryCodes={recoveryCodes} isSetupConfirmation />
      </When>
      <When predicate={currentStep === STEPS.VALIDATION_EXPIRED}>
        <Message message={messages.validationExpiredSubtitle} />
        <Button styled="primary" type="button" onClick={onRestartSetup}>
          <Message message={messages.enableMFAButton} />
        </Button>
      </When>
    </Card>
  );
};

MFASetupModal.propTypes = {
  privateKey: PropTypes.string.isRequired,
  qrcodeURI: PropTypes.string.isRequired,
  onMFAActivatedSucessfully: PropTypes.func.isRequired,
  onRestartSetup: PropTypes.func.isRequired,
};

export default MFASetupModal;
